import type { JSONContent } from "@tiptap/core";
import type { ListContent, PhrasingContent, RootContent } from "mdast";
import { paragraphJson, phrasingToContent } from "./markdownToTiptapPhrasing";

const escapeHtmlBody = (s: string): string =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const listItemToTiptap = (
  item: ListContent,
  segments: string[],
  emitHorizontalRules: boolean,
): JSONContent | null => {
  if (item.type !== "listItem" || !("children" in item)) {
    return null;
  }
  const itemContent: JSONContent[] = [];
  for (const child of item.children as Array<RootContent | PhrasingContent>) {
    if (child.type === "paragraph" && "children" in child) {
      const inner = phrasingToContent(child.children, segments);
      itemContent.push(
        inner.length === 0
          ? { type: "paragraph" }
          : { type: "paragraph", content: inner },
      );
    } else if (child.type === "list" && "children" in child) {
      itemContent.push(
        listNodeToTiptap(
          child as RootContent & {
            type: "list";
            ordered?: boolean;
            children: ListContent[];
          },
          segments,
          emitHorizontalRules,
        ),
      );
    }
  }
  return { type: "listItem", content: itemContent };
};

const listNodeToTiptap = (
  node: RootContent & {
    type: "list";
    ordered?: boolean;
    children: ListContent[];
  },
  segments: string[],
  emitHorizontalRules: boolean,
): JSONContent => {
  const listType = node.ordered === true ? "orderedList" : "bulletList";
  const items = node.children
    .map((item) => listItemToTiptap(item, segments, emitHorizontalRules))
    .filter((x): x is JSONContent => x != null);
  return { type: listType, content: items };
};

export const blockNodeToTiptap = (
  node: RootContent,
  segments: string[],
  emitHorizontalRules: boolean,
): JSONContent[] => {
  if (node.type === "paragraph" && "children" in node) {
    return [paragraphJson(node.children, segments)];
  }
  if (node.type === "thematicBreak") {
    if (!emitHorizontalRules) {
      return [];
    }
    return [{ type: "horizontalRule" }];
  }
  if (node.type === "heading" && "children" in node) {
    const h = node;
    const para = paragraphJson(h.children, segments);
    if (!emitHorizontalRules || h.depth !== 2) {
      return [para];
    }
    return [para, { type: "horizontalRule" }];
  }
  if (node.type === "list" && "children" in node) {
    return [
      listNodeToTiptap(
        node as RootContent & {
          type: "list";
          ordered?: boolean;
          children: ListContent[];
        },
        segments,
        emitHorizontalRules,
      ),
    ];
  }
  if (node.type === "code" && "value" in node) {
    const c = node;
    const escaped = escapeHtmlBody(c.value);
    const lines = escaped.split("\n");
    const content: JSONContent[] = [];
    for (let i = 0; i < lines.length; i++) {
      if (i > 0) {
        content.push({ type: "hardBreak" });
      }
      const line = lines[i] ?? "";
      if (line !== "") {
        content.push({ type: "text", text: line });
      }
    }
    return [
      content.length === 0
        ? { type: "paragraph" }
        : { type: "paragraph", content },
    ];
  }
  return [];
};
